import { createStore, useStoreOf } from './store';

export type Lang = 'en' | 'ar';
export type Theme = 'system' | 'light' | 'dark';

const KEY = 'xr-admin-prefs'; // same key as the prototype

function load(): { lang: Lang; theme: Theme } {
  const out = { lang: 'en' as Lang, theme: 'system' as Theme };
  try {
    const p = JSON.parse(localStorage.getItem(KEY) || '{}');
    if (p.lang === 'ar' || p.lang === 'en') out.lang = p.lang;
    if (['system', 'light', 'dark'].includes(p.theme)) out.theme = p.theme;
  } catch { /* storage blocked */ }
  return out;
}

export const prefs = createStore(load());
export const usePrefs = () => useStoreOf(prefs);

function save() {
  try { localStorage.setItem(KEY, JSON.stringify(prefs.get())); } catch { /* storage blocked */ }
}

export function applyPrefs() {
  const { lang, theme } = prefs.get();
  const de = document.documentElement;
  de.lang = lang;
  de.dir = lang === 'ar' ? 'rtl' : 'ltr';
  if (theme === 'system') de.removeAttribute('data-theme');
  else de.setAttribute('data-theme', theme);
}

export const isDark = () => {
  const t = prefs.get().theme;
  return t === 'dark' || (t === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
};
export function setLang(lang: Lang) { prefs.set({ lang }); save(); applyPrefs(); }
export function toggleLang() { setLang(prefs.get().lang === 'ar' ? 'en' : 'ar'); }
export function toggleTheme() { prefs.set({ theme: isDark() ? 'light' : 'dark' }); save(); applyPrefs(); }

/** Two languages written side by side, as in the prototype: L('Save', 'حفظ'). */
export const L = (en: string, ar?: string | null): string => (prefs.get().lang === 'ar' && ar != null ? ar : en);
/** A name object { en, ar } in the current language. */
export const N = (en: string | null | undefined, ar?: string | null): string => (prefs.get().lang === 'ar' && ar ? ar : en || '');

/* Numbers and dates: Western digits in both languages, as the prototype. */
export const loc = () => (prefs.get().lang === 'ar' ? 'ar-AE-u-nu-latn' : 'en-GB');
export const fDate = (t: number | string | Date, o: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }) =>
  new Date(t).toLocaleDateString(loc(), o);
